// MarketingPlansPanel.jsx — v119.92
// Upload moved to Settings → Uploads (UploadPanel) for a unified "Roots"
// flow. This panel only lists, analyses, approves and manages existing
// master marketing plan files.
import React, { useEffect, useState, useCallback } from "react";
import { api } from "./lib/api";
import { toast } from "sonner";
import { zVar } from "./lib/zIndex";
import { InfoTip } from "./components/InfoTip";
import { formatKSADate } from "./lib/ksaTime";

// ──────────────────────────────────────────────────────────────────────────
// Format helpers
const fmt = (n) => (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return formatKSADate(new Date(iso), { year: "numeric", month: "long" }); }
  catch { return iso.slice(0, 10); }
};
const statusBadge = (s) => {
  const map = {
    pending: { bg: "#fef3c7", fg: "#92400e", label: "⏳ بانتظار الاعتماد" },
    approved: { bg: "#dcfce7", fg: "#166534", label: "✅ معتمدة — في التنفيذ" },
    archived: { bg: "#f3f4f6", fg: "#374151", label: "📦 مؤرشفة" },
  };
  const m = map[s] || map.pending;
  return (
    <span style={{ background: m.bg, color: m.fg, padding: "3px 10px",
                   borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
      {m.label}
    </span>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Main panel
export default function MarketingPlansPanel() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});  // id → bool
  const [details, setDetails] = useState({});    // id → full plan
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketing-plans");
      setPlans(r.data?.items || []);
    } catch (_e) { setPlans([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // v119.92 — listen for the centralised upload event fired by App.js when
  // the admin uploads a new plan from Settings → Uploads → "الخطة التسويقية".
  useEffect(() => {
    const handler = () => { load(); };
    window.addEventListener("tw-marketing-plan-uploaded", handler);
    return () => window.removeEventListener("tw-marketing-plan-uploaded", handler);
  }, [load]);

  const goToUploads = () => {
    // Settings tab → "files" CollapsibleSection holds the Marketing Plan card.
    window.dispatchEvent(new CustomEvent("tw-navigate", { detail: { tab: "st" } }));
    // Defer the open-section + scroll until AFTER Settings mounts (the
    // CollapsibleSection listener is registered in useEffect, so dispatching
    // synchronously loses the event — see iter147 RCA).
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("tw-open-section", { detail: { id: "files" } }));
      // Give the collapse animation a moment, then scroll the card into view.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="upload-card-mkt_plan"]');
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120);
    }, 250);
  };

  const toggleExpand = async (id) => {
    const next = !expanded[id];
    setExpanded((p) => ({ ...p, [id]: next }));
    if (next && !details[id]) {
      try {
        const r = await api.get(`/marketing-plans/${id}`);
        setDetails((p) => ({ ...p, [id]: r.data }));
      } catch (_e) { /* ignore */ }
    }
  };

  const approve = async (id) => {
    if (!window.confirm("اعتماد الخطة بكاملها وتحويل جميع بنودها إلى حملات دفعة واحدة؟ (يمكنك بدلاً من ذلك اعتماد كل بند على حدة من داخل التفاصيل.)")) return;
    setActing(id);
    try {
      const r = await api.post(`/marketing-plans/${id}/approve`, {});
      toast.success(`✅ تم اعتماد الخطة كاملة وإنشاء ${r.data?.campaigns_created || 0} حملة`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "فشل الاعتماد"); }
    setActing(null);
  };

  const remove = async (id) => {
    if (!window.confirm("حذف الخطة نهائياً؟ لا يمكن التراجع.")) return;
    setActing(id);
    try {
      await api.delete(`/marketing-plans/${id}`);
      toast.success("تم الحذف");
      setDetails((p) => { const n = { ...p }; delete n[id]; return n; });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "فشل الحذف"); }
    setActing(null);
  };

  // v119.95 — Quick KPI strip aggregating the loaded plans.
  const kpis = React.useMemo(() => {
    const total = plans.length;
    const approved = plans.filter((p) => p.status === "approved").length;
    const pending = plans.filter((p) => p.status === "pending").length;
    const archived = plans.filter((p) => p.status === "archived").length;
    const approvedBudget = plans
      .filter((p) => p.status === "approved")
      .reduce((sum, p) => sum + (Number(p.budget_total) || 0), 0);
    const pendingBudget = plans
      .filter((p) => p.status === "pending")
      .reduce((sum, p) => sum + (Number(p.budget_total) || 0), 0);
    const totalProjects = plans.reduce(
      (sum, p) => sum + (Array.isArray(p.projects) ? p.projects.length : 0), 0
    );
    return { total, approved, pending, archived, approvedBudget, pendingBudget, totalProjects };
  }, [plans]);

  return (
    <div data-testid="mk-plans-panel" style={{ display: "grid", gap: 14 }}>
      {/* Hero — upload is centralised in Settings → Uploads (v119.92) */}
      <div className="card" style={{
        background: "linear-gradient(135deg, #4338ca 0%, #6366f1 60%, #ec4899 120%)",
        color: "#fff", padding: 20, margin: 0, borderColor: "transparent",
      }}>
        <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 700 }}>📋 v119.92</div>
        <h2 style={{ margin: "4px 0", fontSize: 24, fontWeight: 900, letterSpacing: -0.5 }}>
          الخطط التسويقية — Master Plan
        </h2>
        <div style={{ fontSize: 12.5, opacity: 0.92, marginBottom: 14, lineHeight: 1.7 }}>
          راجع الخطط المرفوعة، حلّل تداخلاتها مع خططٍ سابقة، واعتمدها لتتحوّل تلقائياً إلى
          حملات قابلة للقياس. <strong>رفع ملف الخطة</strong> صار من «الإعدادات ← رفع الملفات ← 📋 الخطة التسويقية»
          لتوحيد مصدر البيانات (Roots).
        </div>
        <button
          type="button"
          onClick={goToUploads}
          data-testid="mk-plans-goto-uploads"
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "#fff", color: "#4338ca", padding: "8px 16px",
            borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: "pointer",
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)", border: "none",
          }}
        >
          ⚙ اذهب إلى رفع الملفات
        </button>
      </div>

      {/* v119.95 — Quick KPIs */}
      {!loading && plans.length > 0 && (
        <div
          data-testid="mk-plans-kpi-strip"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}
        >
          <KpiTile testid="mk-plans-kpi-total" icon="📋" label="إجمالي الخطط"
            value={fmt(kpis.total)}
            sub={`${fmt(kpis.totalProjects)} مشروع مُستخرَج`} color="#4338ca" />
          <KpiTile testid="mk-plans-kpi-approved" icon="✅" label="معتمدة — قيد التنفيذ"
            value={fmt(kpis.approved)}
            sub={kpis.total > 0 ? `${Math.round((kpis.approved / kpis.total) * 100)}% من الإجمالي` : "—"}
            color="#16A34A" />
          <KpiTile testid="mk-plans-kpi-pending" icon="⏳" label="بانتظار الاعتماد"
            value={fmt(kpis.pending)}
            sub={kpis.pendingBudget > 0 ? `${fmt(kpis.pendingBudget)} ر.س معلّقة` : "لا توجد مبالغ معلّقة"}
            color="#D97706" />
          <KpiTile testid="mk-plans-kpi-budget" icon="💰" label="ميزانية الخطط المعتمدة"
            value={`${fmt(kpis.approvedBudget)} ر`}
            sub={kpis.approved > 0 ? `متوسط ${fmt(kpis.approvedBudget / kpis.approved)} ر.س/خطة` : "—"}
            color="#EC4899" />
        </div>
      )}

      {/* Plans list */}
      {loading ? (
        <div className="card" style={{ padding: 30, textAlign: "center", color: "var(--muted)" }}>
          ⏳ جارٍ تحميل الخطط...
        </div>
      ) : plans.length === 0 ? (
        <div className="card" data-testid="mk-plans-empty"
          style={{ padding: 30, textAlign: "center", color: "var(--muted)" }}>
          📭 لا توجد خطط مرفوعة بعد. اذهب إلى «الإعدادات ← رفع الملفات ← 📋 الخطة التسويقية» لرفع أول خطة.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {plans.map((p) => (
            <PlanCard key={p.id} plan={p}
              expanded={!!expanded[p.id]} details={details[p.id]}
              onToggle={() => toggleExpand(p.id)}
              onApprove={() => approve(p.id)}
              onRemove={() => remove(p.id)}
              acting={acting === p.id}
              reload={async () => {
                // Refresh both list and this plan's details after per-project approval.
                await load();
                try {
                  const r = await api.get(`/marketing-plans/${p.id}`);
                  setDetails((prev) => ({ ...prev, [p.id]: r.data }));
                } catch (_e) { /* ignore */ }
              }} />
          ))}
        </div>
      )}
    </div>
  );
}

const projectStatusPill = (s) => {
  const map = {
    pending: { bg: "#fef3c7", fg: "#92400e", label: "⏳ بانتظار الاعتماد" },
    approved: { bg: "#dcfce7", fg: "#166534", label: "✅ معتمد" },
  };
  const m = map[s] || map.pending;
  return (
    <span style={{ background: m.bg, color: m.fg, padding: "2px 8px",
                   borderRadius: 999, fontSize: 10.5, fontWeight: 700 }}>
      {m.label}
    </span>
  );
};

function BulkApproveButton({ planId, pendingCount, onApproved }) {
  const [busy, setBusy] = React.useState(false);
  const doApproveAll = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `اعتماد ${pendingCount} بنود دفعة واحدة؟ سيقوم Claude باقتراح مقاييس الأثر والميزانية تلقائياً لكل بند.`,
    )) return;
    setBusy(true);
    try {
      const r = await api.post(`/marketing-plans/${planId}/approve-all`, {});
      const okN = r.data?.approved?.length || 0;
      const errN = r.data?.failed?.length || 0;
      if (errN > 0) {
        toast.warn(`✅ اعتُمد ${okN} بنداً · ⚠️ فشل ${errN} — راجع السجلات.`);
      } else {
        toast.success(`✅ اعتُمدت كل ${okN} بنود بنجاح وأُنشئت حملات لكل واحد.`);
      }
      onApproved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل اعتماد الخطة");
    }
    setBusy(false);
  };
  return (
    <div style={{ marginBottom: 8, padding: 10, background: "#ecfdf5",
                     border: "1px solid #059669", borderRadius: 6,
                     display: "flex", alignItems: "center", justifyContent: "space-between",
                     gap: 8, flexWrap: "wrap" }}
         data-testid={`mk-plan-${planId}-approve-all-block`}>
      <div style={{ fontSize: 12, color: "#065f46", fontWeight: 700 }}>
        🚀 <b>{pendingCount}</b> بنود بحاجة للاعتماد — Claude جاهز لاقتراح المقاييس تلقائياً.
      </div>
      <button type="button" onClick={doApproveAll} disabled={busy}
        data-testid={`mk-plan-${planId}-approve-all`}
        style={{ background: "#059669", color: "#fff", border: "none",
                  borderRadius: 6, padding: "8px 16px", fontSize: 12.5,
                  fontWeight: 800, cursor: busy ? "wait" : "pointer" }}>
        {busy ? "⏳ يعتمد الجميع..." : "✅ اعتمد الخطة كاملة (بنقرة واحدة)"}
      </button>
    </div>
  );
}


function ProjectRow({ project, planId, onApproved }) {
  const [open, setOpen] = React.useState(false);
  const [approving, setApproving] = React.useState(false);
  const [regenBusy, setRegenBusy] = React.useState(false);
  const [regenOpen, setRegenOpen] = React.useState(false);
  const [aiOverride, setAiOverride] = React.useState(null);

  const approved = project.status === "approved";
  const ai = aiOverride || project.ai_extracted || null;

  const doRegen = async (hint) => {
    setRegenBusy(true);
    setRegenOpen(false);
    try {
      const r = await api.post(
        `/marketing-plans/${planId}/projects/${project.id}/re-enrich`,
        { hint },
      );
      setAiOverride(r.data?.ai_extracted || null);
      toast.success("🔄 اقتراح جديد جاهز من Claude");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إعادة التوليد");
    }
    setRegenBusy(false);
  };

  const submit = async () => {
    setApproving(true);
    try {
      const r = await api.post(
        `/marketing-plans/${planId}/projects/${project.id}/approve`,
        {},  // v119.129 — backend uses ai_extracted values automatically
      );
      toast.success(`✅ اعتماد بند «${project.name}» وإنشاء ${r.data?.tasks_created || 0} مهمة على الموظفين`);
      onApproved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل اعتماد البند");
    }
    setApproving(false);
  };

  return (
    <div data-testid={`mk-proj-${project.id}`} style={{
      padding: 12, background: "#fff", borderRadius: 8,
      borderInlineStart: `3px solid ${approved ? "#059669" : "#6366f1"}`,
      display: "grid", gap: 8,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <div style={{ fontWeight: 800, color: "var(--brown-deep)", fontSize: 13 }}>{project.name}</div>
            {projectStatusPill(approved ? "approved" : "pending")}
          </div>
          <div style={{ color: "var(--muted)", fontSize: 11 }}>
            📋 {project.task_count} مهمة
            {project.owners?.length > 0 && ` · 👤 ${project.owners.slice(0, 3).join("، ")}`}
          </div>
          {project.channels?.length > 0 && (
            <div style={{ color: "var(--muted)", fontSize: 11, marginTop: 2 }}>
              📡 {project.channels.slice(0, 3).join(" · ")}
            </div>
          )}
          {approved && project.start_date && (
            <div style={{ marginTop: 4, fontSize: 11, color: "#166534", fontWeight: 700 }}>
              🚀 بدأ في: {project.start_date} {project.budget ? `· 💰 ${fmt(project.budget)} ر.س` : ""}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 5 }}>
          <button type="button" onClick={() => setOpen(!open)} data-testid={`mk-proj-${project.id}-toggle`}
            style={{ background: "#fff", border: "1px solid #ddd", borderRadius: 6,
                      padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>
            {open ? "🔼" : "📋 مهام"}
          </button>
          {!approved && (
            <button type="button" onClick={submit} disabled={approving}
              data-testid={`mk-proj-${project.id}-approve-toggle`}
              style={{ background: "#059669", color: "#fff", border: "none", borderRadius: 6,
                        padding: "4px 12px", fontSize: 11, fontWeight: 800,
                        cursor: approving ? "wait" : "pointer" }}>
              {approving ? "⏳ يعتمد..." : "✅ اعتمد البند"}
            </button>
          )}
        </div>
      </div>

      {/* v119.129 — AI-extracted preview (shown only for pending items) */}
      {!approved && ai && (
        <div data-testid={`mk-proj-${project.id}-ai-preview`}
             style={{ padding: 10, background: "#fef9e7", borderRadius: 6,
                        borderInlineStart: "3px solid #C9A227", display: "grid", gap: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", flexWrap: "wrap", gap: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#7B5804" }}>
              🤖 اقتراح المستشار التحليلي (Claude) — اعتمد بنقرة واحدة
            </div>
            <div style={{ position: "relative" }}>
              <button type="button"
                onClick={() => setRegenOpen(!regenOpen)}
                disabled={regenBusy}
                data-testid={`mk-proj-${project.id}-regen-toggle`}
                style={{ background: "#fff", border: "1px solid #C9A227",
                          borderRadius: 4, padding: "2px 8px", fontSize: 10.5,
                          fontWeight: 800, color: "#7B5804",
                          cursor: regenBusy ? "wait" : "pointer" }}>
                {regenBusy ? "⏳ يعيد التوليد..." : "🔄 اقتراح جديد ▾"}
              </button>
              {regenOpen && !regenBusy && (
                <div data-testid={`mk-proj-${project.id}-regen-menu`}
                  style={{ position: "absolute", top: "100%", insetInlineEnd: 0,
                            background: "#fff", border: "1px solid #C9A227", borderRadius: 6,
                            marginTop: 4, minWidth: 200, zIndex: zVar('extra_10'),
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
                  {[
                    ["more_ambitious", "⚡ أهداف أطموح"],
                    ["less_budget",    "💰 ميزانية أقل"],
                    ["faster",         "⏱ ابدأ مبكّراً"],
                    ["conservative",   "🛡 أهداف محافظة"],
                    ["",               "🎲 توليد جديد بحرية"],
                  ].map(([h, label]) => (
                    <button key={h || "free"} type="button"
                      onClick={() => doRegen(h)}
                      data-testid={`mk-proj-${project.id}-regen-${h || "free"}`}
                      style={{ display: "block", width: "100%",
                                textAlign: "right", padding: "6px 10px",
                                background: "transparent", border: "none",
                                fontSize: 11.5, cursor: "pointer",
                                borderBottom: "1px solid #f5f5f5" }}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                          gap: 6, fontSize: 11.5 }}>
            <div><b>🎯 مقياس الأثر:</b> {ai.impact_metric || "—"}</div>
            <div><b>📊 الهدف:</b> {ai.impact_target ? Number(ai.impact_target).toLocaleString() : "—"}</div>
            <div><b>💰 الميزانية:</b> {ai.budget ? `${Number(ai.budget).toLocaleString()} ر.س` : "—"}</div>
            <div><b>📅 البدء:</b> {ai.start_date || "—"}</div>
          </div>
          {ai.reasoning && (
            <div style={{ fontSize: 10.5, color: "var(--muted)", fontStyle: "italic" }}>
              💡 {ai.reasoning}
            </div>
          )}
        </div>
      )}
      {!approved && !ai && (
        <div style={{ padding: 8, background: "#fef3c7", borderRadius: 6,
                        fontSize: 11, color: "#92400e" }}>
          ⏳ لم يُحلَّل هذا البند بعد. اضغط «اعتمد البند» وسيقوم Claude بالاستخراج تلقائياً.
        </div>
      )}

      {open && (project.tasks || []).length > 0 && (
        <div style={{ display: "grid", gap: 3, maxHeight: 200, overflowY: "auto", background: "#FAFAFA", padding: 8, borderRadius: 4 }}>
          {(project.tasks || project.sample_tasks || []).map((t, i) => (
            <div key={i} style={{ fontSize: 11, padding: "4px 6px", background: "#fff", borderRadius: 3 }}>
              <b>{typeof t === "string" ? t : t.task}</b>
              {typeof t !== "string" && (
                <span style={{ color: "var(--muted)", marginInlineStart: 6 }}>
                  {t.owner && `· 👤 ${t.owner}`} {t.deadline && `· 📅 ${t.deadline}`}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// v119.123 — Mini Gantt: horizontal timeline of approved projects, with
// buffer gaps highlighted (3-5 day recommendation from AI) and overlap flag.
function MiniGantt({ plan, projects }) {
  const approved = (projects || []).filter((p) => p.status === "approved" && p.start_date);
  if (approved.length === 0) return null;

  const parse = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const planStart = parse(plan.period_start) || approved.reduce((m, p) => {
    const d = parse(p.start_date); return d && (!m || d < m) ? d : m;
  }, null);
  const planEnd = parse(plan.period_end) || (() => {
    const s = parse(planStart); if (!s) return null;
    return new Date(s.getTime() + 30 * 24 * 3600 * 1000);
  })();
  if (!planStart || !planEnd) return null;
  const totalDays = Math.max(1, Math.ceil((planEnd - planStart) / (24 * 3600 * 1000)));

  // Sort projects by start_date and detect overlaps
  const sorted = [...approved].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  const bars = sorted.map((p, i) => {
    const s = parse(p.start_date);
    const dur = Math.max(3, Math.min(7, Math.round(p.task_count / 3)));  // heuristic
    const e = new Date(s.getTime() + dur * 24 * 3600 * 1000);
    const startOffset = Math.max(0, Math.round((s - planStart) / (24 * 3600 * 1000)));
    const width = Math.max(2, Math.round((e - s) / (24 * 3600 * 1000)));
    return {
      id: p.id,
      name: p.name,
      startOffset,
      width,
      color: "#C9A227",
      task_count: p.task_count,
      budget: p.budget,
    };
  });

  // Check gaps between consecutive projects
  const gaps = [];
  for (let i = 0; i < bars.length - 1; i++) {
    const gap = bars[i + 1].startOffset - (bars[i].startOffset + bars[i].width);
    if (gap < 3) gaps.push({ after: bars[i].id, days: gap });
  }

  return (
    <div data-testid={`mk-plan-${plan.id}-gantt`} style={{
      padding: 12, background: "linear-gradient(135deg,#FBF7F0,#fff)",
      borderRadius: 8, border: "1px solid #C9A227",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 800, color: "var(--brown-deep)", fontSize: 13 }}>
          📊 جدول جانت مصغّر — البنود المعتمدة على خط الزمن
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "var(--muted)" }}>
            {plan.period_start} → {plan.period_end}
          </span>
          <a href={`${(process.env.REACT_APP_BACKEND_URL || '')}/api/marketing-plans/${plan.id}/gantt.pdf`}
            target="_blank" rel="noreferrer"
            data-testid={`mk-plan-${plan.id}-gantt-pdf`}
            style={{ padding: "4px 10px", background: "#3E2723", color: "#FBF7F0",
                      borderRadius: 4, fontSize: 11, fontWeight: 700, textDecoration: "none" }}>
            📄 تصدير PDF
          </a>
        </div>
      </div>
      {/* Timeline */}
      <div style={{ position: "relative", height: bars.length * 26 + 16, background: "#fff",
                      borderRadius: 6, padding: "6px 8px", border: "1px solid #eee",
                      overflow: "hidden" }}>
        {/* Week gridlines */}
        {Array.from({ length: Math.floor(totalDays / 7) + 1 }).map((_, w) => (
          <div key={w} style={{
            position: "absolute", top: 0, bottom: 0,
            insetInlineStart: `${(w * 7 / totalDays) * 100}%`,
            width: 1, background: "#eee",
          }} />
        ))}
        {bars.map((b, i) => (
          <div key={b.id} style={{
            position: "absolute", top: 8 + i * 26, height: 20,
            insetInlineStart: `${(b.startOffset / totalDays) * 100}%`,
            width: `${(b.width / totalDays) * 100}%`,
            background: b.color, borderRadius: 4, display: "flex", alignItems: "center",
            padding: "0 6px", fontSize: 10.5, color: "#3E2723", fontWeight: 700,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            boxShadow: "0 1px 2px rgba(0,0,0,.1)",
 }} ><InfoTip text={`${b.name} · ${b.task_count} مهمة · بدء ${sorted[i].start_date}`} />
            {b.name}
          </div>
        ))}
      </div>
      {gaps.length > 0 && (
        <div style={{ marginTop: 8, padding: 8, background: "#fee2e2", borderRadius: 6, fontSize: 11.5, color: "#991b1b" }}>
          ⚠️ {gaps.length} فجوة أقل من 3 أيام بين البنود — يوصي AI بترك buffer 3-5 أيام لتلافي تداخل التنفيذ.
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
function PlanCard({ plan, expanded, details, onToggle, onApprove, onRemove, acting, reload }) {
  const overlapCount = (plan.overlap_with || []).length;
  const isPending = plan.status === "pending";
  return (
    <div className="card" data-testid={`mk-plan-${plan.id}`} style={{
      padding: 14, margin: 0,
      borderInlineStart: `4px solid ${isPending ? "#f59e0b" : plan.status === "approved" ? "#059669" : "#9ca3af"}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                     alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontSize: 17, fontWeight: 900, color: "var(--brown-deep)" }}>
              📅 {plan.period_label || "—"}
            </div>
            {statusBadge(plan.status || "pending")}
            {overlapCount > 0 && (
              <span data-testid={`mk-plan-${plan.id}-overlap-badge`}
                style={{ background: "#fee2e2", color: "#991b1b",
                          padding: "3px 10px", borderRadius: 999,
                          fontSize: 11, fontWeight: 700 }}>
                ⚠️ تداخل مع {overlapCount} خطة
              </span>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 4 }}>
            📄 {plan.filename || "—"} · رفعها {plan.uploaded_by || "—"} ·
            {" "}{formatKSADate(new Date(plan.uploaded_at))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {isPending && (
            <button className="btn btn-dark" disabled={acting}
              onClick={onApprove}
              data-testid={`mk-plan-${plan.id}-approve`}
              style={{ fontSize: 12, padding: "5px 12px" }}>
              {acting ? "⏳" : "✅ اعتماد"}
            </button>
          )}
          <button className="btn btn-soft" onClick={onToggle}
            data-testid={`mk-plan-${plan.id}-toggle`}
            style={{ fontSize: 12, padding: "5px 10px" }}>
            {expanded ? "🔼 إخفاء" : "📖 عرض التفاصيل"}
          </button>
          <button onClick={onRemove} disabled={acting}
            data-testid={`mk-plan-${plan.id}-delete`}
            style={{ background: "none", border: "none",
                      color: "#dc2626", cursor: "pointer", fontSize: 12 }}>
            🗑
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                     gap: 8, marginBottom: 8 }}>
        <StatPill icon="🎯" label="مشاريع" value={(plan.projects || []).length} />
        <StatPill icon="📅" label="يوم محتوى" value={(plan.sheets || []).find(s => s.type === "daily_content")?.parsed || 0} />
        <StatPill icon="📋" label="مهام" value={(plan.sheets || []).filter(s => s.type === "tasks").reduce((acc, s) => acc + (s.parsed || 0), 0)} />
        <StatPill icon="📊" label="KPIs" value={(plan.sheets || []).find(s => s.type === "kpis")?.parsed || 0} />
        <StatPill icon="💰" label="الميزانية" value={`${fmt(plan.budget_total)} ر.س`} />
      </div>

      {/* AI Analysis preview */}
      {plan.ai_analysis && (
        <div style={{ padding: 10, background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                       borderRadius: 8, fontSize: 12.5, color: "#78350f",
                       whiteSpace: "pre-wrap", lineHeight: 1.8,
                       maxHeight: expanded ? "none" : 140, overflow: "hidden",
                       position: "relative" }}
          data-testid={`mk-plan-${plan.id}-ai`}>
          <div style={{ fontWeight: 800, marginBottom: 4, color: "#92400e" }}>
            🤖 تحليل المستشار التسويقي (Claude):
          </div>
          {plan.ai_analysis}
          {!expanded && plan.ai_analysis.length > 200 && (
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0,
                            height: 40,
                            background: "linear-gradient(to top, #fde68a 30%, transparent)" }} />
          )}
        </div>
      )}

      {/* Expanded details */}
      {expanded && details && (
        <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
          {/* v119.123: mini Gantt visualising approved projects on the plan's timeline */}
          <MiniGantt plan={plan} projects={details.projects || []} />

          {/* Projects breakdown — each is independently approvable (v119.122) */}
          {(details.projects || []).length > 0 && (
            <Section title={`🎯 بنود الخطة (${details.projects.length}) — كل بند يُعتمد باستقلال`}
              testid={`mk-plan-${plan.id}-projects`}>
              {/* v119.129 — One-click bulk-approve all pending projects */}
              {(details.projects || []).some((p) => p.status !== "approved") && (
                <BulkApproveButton planId={plan.id}
                  pendingCount={(details.projects || []).filter((p) => p.status !== "approved").length}
                  onApproved={reload} />
              )}
              <div style={{ display: "grid", gap: 8 }}>
                {details.projects.map((p) => (
                  <ProjectRow key={p.id || p.name} project={p} planId={plan.id}
                    onApproved={reload} />
                ))}
              </div>
            </Section>
          )}

          {/* Tasks sample */}
          {(details.tasks || []).length > 0 && (
            <Section title={`📋 عيّنة من المهام (${details.tasks.length} مهمة)`}
              testid={`mk-plan-${plan.id}-tasks`}>
              <div style={{ display: "grid", gap: 4, maxHeight: 280, overflowY: "auto" }}>
                {details.tasks.slice(0, 30).map((t, i) => (
                  <div key={i} style={{ padding: 6, background: "#fff", borderRadius: 4,
                                          fontSize: 11.5, display: "flex", gap: 8 }}>
                    <div style={{ flex: 1 }}>{t.task}</div>
                    <div style={{ color: "var(--muted)", whiteSpace: "nowrap" }}>
                      {t.owner && `👤 ${t.owner}`}
                      {t.deadline && ` · 📅 ${t.deadline}`}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Budget items */}
          {(details.budget_items || []).length > 0 && (
            <Section title={`💰 بنود الميزانية (الإجمالي ${fmt(details.budget_total)} ر.س)`}
              testid={`mk-plan-${plan.id}-budget`}>
              <table style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f3f4f6", textAlign: "right" }}>
                    <th style={{ padding: 6 }}>البند</th>
                    <th style={{ padding: 6 }}>القناة</th>
                    <th style={{ padding: 6, textAlign: "left" }}>الإجمالي</th>
                  </tr>
                </thead>
                <tbody>
                  {details.budget_items.map((b, i) => (
                    <tr key={i} style={{ borderTop: "1px solid #e5e7eb" }}>
                      <td style={{ padding: 6 }}>{b.item}</td>
                      <td style={{ padding: 6, color: "var(--muted)" }}>{b.channel || "—"}</td>
                      <td style={{ padding: 6, textAlign: "left", fontWeight: 700 }}>
                        {fmt(b.total)} ر.س
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {/* KPIs */}
          {(details.kpis || []).length > 0 && (
            <Section title={`📊 KPIs (${details.kpis.length})`}
              testid={`mk-plan-${plan.id}-kpis`}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                              gap: 6 }}>
                {details.kpis.slice(0, 24).map((k, i) => (
                  <div key={i} style={{ padding: 6, background: "#fff", borderRadius: 4,
                                          fontSize: 11.5 }}>
                    <div style={{ color: "var(--brown-deep)" }}>{k.metric}</div>
                    <div style={{ color: "#059669", fontWeight: 700, fontSize: 11 }}>
                      🎯 {k.target}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Daily content sample */}
          {(details.daily_content || []).length > 0 && (
            <Section title={`📅 جدول المحتوى اليومي (${details.daily_content.length} يوم)`}
              testid={`mk-plan-${plan.id}-daily`}>
              <div style={{ display: "grid", gap: 4, maxHeight: 260, overflowY: "auto" }}>
                {details.daily_content.slice(0, 14).map((d, i) => (
                  <div key={i} style={{ padding: 8, background: "#fff", borderRadius: 6,
                                          fontSize: 11.5 }}>
                    <div style={{ fontWeight: 800, color: "var(--brown-deep)" }}>
                      {d.date_label}
                    </div>
                    <div style={{ color: "var(--muted)", marginTop: 2, fontSize: 11,
                                    display: "grid", gap: 2 }}>
                      {Object.entries(d).filter(([k]) => k !== "date_label").slice(0, 4).map(([k, v]) => (
                        <div key={k}>• <strong>{k.split("\n")[0]}:</strong> {String(v).slice(0, 100)}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {plan.status === "approved" && plan.campaign_ids && (
            <div style={{ padding: 8, background: "#dcfce7", borderRadius: 6,
                            fontSize: 12, color: "#166534" }}>
              ✅ تم إنشاء {plan.campaign_ids.length} حملة في تبويب «الحملات» — يُقاس أثرها تلقائياً.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatPill({ icon, label, value }) {
  return (
    <div style={{ padding: 8, background: "var(--beige)", borderRadius: 6, textAlign: "center" }}>
      <div style={{ fontSize: 10.5, color: "var(--muted)" }}>{icon} {label}</div>
      <div style={{ fontSize: 14, fontWeight: 900, color: "var(--brown-deep)" }}>{value}</div>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div data-testid={testid} style={{ padding: 10, background: "var(--beige)", borderRadius: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: "var(--brown-deep)",
                     marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

// v119.95 — Compact KPI tile used by the at-a-glance strip at the top
// of the Marketing Plans page.
function KpiTile({ icon, label, value, sub, color = "#4338ca", testid }) {
  return (
    <div className="card" data-testid={testid} style={{
      padding: 12, margin: 0, borderInlineStart: `4px solid ${color}`,
    }}>
      <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700 }}>{icon} {label}</div>
      <div style={{ fontSize: 22, fontWeight: 900, color: "var(--brown-deep)", letterSpacing: -0.5, marginTop: 2 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 1 }}>{sub}</div>}
    </div>
  );
}
